import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Envelope, MagnifyingGlass, Lightning, Warning, CheckCircle, ArrowsCounterClockwise, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatRow({ label, value, color = 'zinc', total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const colorMap = {
    emerald: 'bg-emerald-500',
    rose: 'bg-rose-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    zinc: 'bg-zinc-400',
  };
  return (
    <div className="flex items-center justify-between text-xs py-1.5">
      <span className="text-zinc-600">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-24 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className={`h-full ${colorMap[color]}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="font-data font-semibold text-zinc-800 w-16 text-right">{value.toLocaleString()}</span>
        <span className="text-zinc-400 w-9 text-right">({pct}%)</span>
      </div>
    </div>
  );
}

export default function EmailAuditRescuePanel({ open, onClose, onRefreshContacts }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [normalizingPlaceholders, setNormalizingPlaceholders] = useState(false);
  const [patternStats, setPatternStats] = useState(null);
  const [patternJob, setPatternJob] = useState(null);
  const [junkStats, setJunkStats] = useState(null);
  const [purgingJunk, setPurgingJunk] = useState(false);
  const pollRef = useRef(null);
  const patternPollRef = useRef(null);

  const loadAudit = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/contacts-email-audit`, { credentials: 'include' });
      const d = await r.json();
      setAudit(d);
    } catch (e) {
      toast.error('Kon audit niet laden');
    }
    setLoading(false);
  };

  const loadPatternStats = async () => {
    try {
      const r = await fetch(`${API}/contacts-email-rescue/pattern-stats`, { credentials: 'include' });
      if (r.ok) setPatternStats(await r.json());
    } catch { /* silent */ }
  };

  const loadJunkStats = async () => {
    try {
      const r = await fetch(`${API}/contacts-email-rescue/junk-stats`, { credentials: 'include' });
      if (r.ok) setJunkStats(await r.json());
    } catch { /* silent */ }
  };

  const purgeJunk = async () => {
    if (!junkStats || junkStats.junk_contacts === 0) return;
    const samples = (junkStats.samples || []).slice(0, 3).join(', ');
    if (!window.confirm(`Verwijder ${junkStats.junk_contacts} contacten met tracking-pixel/UUID-emails (zoals: ${samples})?\n\nDe contacten zelf worden permanent verwijderd. Hun bron-facility blijft intact.`)) return;
    setPurgingJunk(true);
    try {
      const r = await fetch(`${API}/contacts-email-rescue/purge-junk`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verwijderen mislukt');
      toast.success(`${d.deleted} junk-contacten verwijderd`);
      await Promise.all([loadAudit(), loadJunkStats()]);
      onRefreshContacts && onRefreshContacts();
    } catch (e) {
      toast.error(e.message);
    }
    setPurgingJunk(false);
  };

  useEffect(() => {
    if (open) {
      loadAudit();
      loadPatternStats();
      loadJunkStats();
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (patternPollRef.current) { clearInterval(patternPollRef.current); patternPollRef.current = null; }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (patternPollRef.current) clearInterval(patternPollRef.current);
    };
  }, [open]);

  const normalizePlaceholders = async () => {
    setNormalizingPlaceholders(true);
    try {
      const r = await fetch(`${API}/contacts-email-rescue/normalize-placeholders`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      toast.success(`${d.normalized} placeholders opgeruimd`);
      loadAudit();
    } catch {
      toast.error('Normalize mislukt');
    }
    setNormalizingPlaceholders(false);
  };

  const startRescue = async (limit) => {
    try {
      const r = await fetch(`${API}/contacts-email-rescue/start?limit=${limit}`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Start mislukt');
      setJob({ ...d, total: limit, processed: 0, rescued: 0, failed: 0 });
      toast.success(`Rescue gestart voor ${limit} contacten`);
      // Poll
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r2 = await fetch(`${API}/contacts-email-rescue/status/${d.job_id}`, { credentials: 'include' });
          const j = await r2.json();
          setJob(j);
          if (j.status === 'done' || j.status === 'error') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.success(`Klaar: ${j.rescued || 0} contacten verrijkt`);
            loadAudit();
            onRefreshContacts && onRefreshContacts();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const startApplyPatterns = async () => {
    if (!window.confirm('Patronen toepassen op alle facilities in dit profiel? Dit kan honderden contacten in één keer vullen.')) return;
    try {
      const r = await fetch(`${API}/contacts-email-rescue/apply-patterns`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Start mislukt');
      setPatternJob({ ...d, processed: 0, total: 0, rescued_via_pattern: 0, facilities_with_pattern: 0 });
      toast.success('Pattern propagation gestart');
      if (patternPollRef.current) clearInterval(patternPollRef.current);
      patternPollRef.current = setInterval(async () => {
        try {
          const r2 = await fetch(`${API}/contacts-email-rescue/status/${d.job_id}`, { credentials: 'include' });
          const j = await r2.json();
          setPatternJob(j);
          if (j.status === 'done' || j.status === 'error') {
            clearInterval(patternPollRef.current);
            patternPollRef.current = null;
            toast.success(`Klaar: ${j.rescued_via_pattern || 0} contacten verrijkt via patronen`);
            loadAudit();
            loadPatternStats();
            onRefreshContacts && onRefreshContacts();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!open) return null;

  const total = audit?.total || 0;
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="email-audit-panel">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Envelope size={16} className="text-emerald-600" />
            <h2 className="text-base font-semibold">Email Audit & Rescue</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="email-audit-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading || !audit ? (
            <div className="text-xs text-zinc-500 italic">Audit laden...</div>
          ) : (
            <>
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">Email kwaliteit — actief profiel</h3>
                  <Button size="sm" variant="outline" onClick={loadAudit} data-testid="audit-refresh-btn">
                    <ArrowsCounterClockwise size={11} className="mr-1.5" /> Verversen
                  </Button>
                </div>
                <div className="bg-zinc-50 rounded-md p-3 border border-zinc-200 space-y-0.5">
                  <StatRow label="Persoonlijk (firstname@...)" value={audit.personal} color="emerald" total={total} />
                  <StatRow label="Generiek (info@, contact@...)" value={audit.generic} color="blue" total={total} />
                  <StatRow label="Ongeldig (syntax fout)" value={audit.invalid} color="amber" total={total} />
                  <StatRow label="Ontbreekt" value={audit.missing} color="rose" total={total} />
                  <div className="border-t border-zinc-200 mt-2 pt-2 flex items-center justify-between text-xs">
                    <span className="font-medium">Totaal contacten</span>
                    <span className="font-data font-semibold">{total.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-700 flex items-center gap-1"><CheckCircle size={11}/> Bruikbaar voor email</span>
                    <span className="font-data font-semibold text-emerald-700">{audit.usable.toLocaleString()} ({pct(audit.usable)}%)</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-amber-700 flex items-center gap-1"><Warning size={11}/> Te redden (heeft facility met website)</span>
                    <span className="font-data font-semibold text-amber-700">{audit.rescuable.toLocaleString()}</span>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Lightning size={14} className="text-amber-500" /> Rescue actie
                </h3>
                <p className="text-xs text-zinc-500 mb-3">
                  We bezoeken de website van het facility waarmee een contact verbonden is, scrapen mailto:-links en
                  contactpagina's, en vullen de gevonden bedrijfsemail (info@, contact@, of een persoonlijk match)
                  automatisch in. Per facility houden we een 7-dagen cache aan om dubbele bezoeken te voorkomen.
                </p>

                {job && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 text-xs" data-testid="rescue-job-status">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        {job.status === 'running' && '⏳ Rescue draait...'}
                        {job.status === 'done' && '✅ Rescue klaar'}
                        {job.status === 'error' && '❌ Rescue gefaald'}
                      </span>
                      <span className="font-data text-zinc-700">{job.processed || 0}/{job.total || 0}</span>
                    </div>
                    <div className="w-full h-1.5 bg-blue-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct(job.processed || 0) === 0 ? 0 : Math.round(((job.processed || 0) / (job.total || 1)) * 100)}%` }} />
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-zinc-600">
                      <span>✓ Verrijkt: <strong className="text-emerald-700">{job.rescued || 0}</strong></span>
                      <span>✗ Niet gevonden: <strong className="text-zinc-600">{job.failed || 0}</strong></span>
                    </div>
                    {job.error && <div className="text-rose-700 text-[10px] mt-1">{job.error}</div>}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {[50, 200, 500, 1000].map((n) => (
                    <Button
                      key={n}
                      size="sm"
                      onClick={() => startRescue(n)}
                      disabled={job?.status === 'running'}
                      className={n === 50 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                      variant={n === 50 ? 'default' : 'outline'}
                      data-testid={`rescue-start-${n}`}
                    >
                      <MagnifyingGlass size={11} className="mr-1.5" /> Rescue {n} contacten
                    </Button>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-400 mt-2 italic">
                  Tip: 50 contacten ≈ 4-6 minuten (politely scraping, max 5 pagina's per facility). Voor 20k contacten run je deze dagelijks 1.000 per dag.
                </p>
              </section>

              <section data-testid="junk-purge-section">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span className="text-rose-500">🗑</span> Tracking-pixel garbage opruimen
                </h3>
                <p className="text-xs text-zinc-500 mb-3">
                  Wix-websites lekken vaak hun error-tracking als nep-emails (bijv. <code className="bg-zinc-100 px-1 rounded text-[10px]">9a65e97...@sentry.wixpress.com</code>). Deze hebben geen sales-waarde en vertroebelen je segmenten. Verwijderen is veilig — bronfacility blijft staan.
                </p>
                {junkStats && (
                  <div className={`rounded-md p-3 mb-3 text-xs border ${junkStats.junk_contacts > 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="junk-stats">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-600">Contacten met junk-emails</span>
                      <span className={`font-data font-semibold text-lg ${junkStats.junk_contacts > 0 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="junk-count">
                        {junkStats.junk_contacts.toLocaleString()}
                      </span>
                    </div>
                    {junkStats.samples && junkStats.samples.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-rose-200 text-[10px] text-zinc-500 font-mono break-all">
                        {junkStats.samples.slice(0, 3).map((s, i) => <div key={i}>· {s}</div>)}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={purgeJunk}
                    disabled={purgingJunk || !junkStats || junkStats.junk_contacts === 0}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="purge-junk-btn"
                  >
                    {purgingJunk ? 'Verwijderen...' : `Verwijder ${junkStats?.junk_contacts || 0} junk-contacten`}
                  </Button>
                  <Button size="sm" variant="outline" onClick={loadJunkStats} data-testid="junk-stats-refresh">
                    <ArrowsCounterClockwise size={11} className="mr-1.5" /> Herscan
                  </Button>
                </div>
              </section>

              <section data-testid="pattern-inference-section">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Lightning size={14} className="text-indigo-500" /> Patroon-inferentie (bulk)
                </h3>
                <p className="text-xs text-zinc-500 mb-3">
                  Na een succesvolle scrape van een persoonlijk emailadres (bijv. <code className="bg-zinc-100 px-1 rounded">j.devries@domein.nl</code>) leiden we het patroon af en vullen we automatisch alle collega's bij dezelfde facility in. Run dit handmatig op het hele profiel om bestaande personal-emails als basis te gebruiken.
                </p>

                {patternStats && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-3 text-xs grid grid-cols-2 gap-3" data-testid="pattern-stats">
                    <div>
                      <div className="text-zinc-500">Facilities met afleidbaar patroon</div>
                      <div className="font-data font-semibold text-indigo-700 text-lg" data-testid="pattern-stats-facilities">
                        {patternStats.facilities_with_pattern.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-500">Collega's die we kunnen vullen</div>
                      <div className="font-data font-semibold text-indigo-700 text-lg" data-testid="pattern-stats-fillable">
                        {patternStats.potential_fillable_contacts.toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}

                {patternJob && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-3 text-xs" data-testid="pattern-job-status">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        {patternJob.status === 'running' && '⏳ Patronen toepassen...'}
                        {patternJob.status === 'done' && '✅ Patroon-propagatie klaar'}
                        {patternJob.status === 'error' && '❌ Patroon-propagatie gefaald'}
                      </span>
                      <span className="font-data text-zinc-700">{patternJob.processed || 0}/{patternJob.total || 0}</span>
                    </div>
                    <div className="w-full h-1.5 bg-indigo-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-indigo-500 transition-all" style={{ width: `${patternJob.total > 0 ? Math.round(((patternJob.processed || 0) / patternJob.total) * 100) : 0}%` }} />
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-zinc-600">
                      <span>✓ Contacten verrijkt: <strong className="text-indigo-700">{patternJob.rescued_via_pattern || 0}</strong></span>
                      <span>Facilities met match: <strong className="text-indigo-700">{patternJob.facilities_with_pattern || 0}</strong></span>
                    </div>
                    {patternJob.error && <div className="text-rose-700 text-[10px] mt-1">{patternJob.error}</div>}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    size="sm"
                    onClick={startApplyPatterns}
                    disabled={patternJob?.status === 'running' || !patternStats || patternStats.facilities_with_pattern === 0}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="apply-patterns-btn"
                  >
                    <Lightning size={11} className="mr-1.5" /> Patronen toepassen op hele profiel
                  </Button>
                  <Button size="sm" variant="outline" onClick={loadPatternStats} data-testid="pattern-stats-refresh">
                    <ArrowsCounterClockwise size={11} className="mr-1.5" /> Statistieken verversen
                  </Button>
                </div>
                <p className="text-[11px] text-zinc-400 mt-2 italic">
                  Werkt enkel op facilities waar al minstens één persoonlijk emailadres bekend is. Reeds bruikbare emails worden nooit overschreven.
                </p>
              </section>

              <section>
                <h3 className="text-sm font-semibold mb-2">Data cleanup</h3>
                <p className="text-xs text-zinc-500 mb-2">
                  Sommige contacten hebben 'None'/'null'/'n/a' als email-string (data corruptie). Deze knop zet ze om naar échte null waardes zodat ze in de "Ontbreekt" categorie vallen.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={normalizePlaceholders}
                  disabled={normalizingPlaceholders}
                  data-testid="normalize-placeholders-btn"
                >
                  {normalizingPlaceholders ? 'Bezig...' : 'Normaliseer placeholder-emails'}
                </Button>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
